import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AutoComplete, Input } from 'antd';

import { Vendor } from '@totem/components/vendors/types';
import VendorLogoContext from '@totem/components/vendors/vendorLogos/vendorLogoContext';
import { isNotNull } from '@totem/utilities/common';

const styles = {
  input: {
    width: '100%',
  },
};

type Props = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  style?: React.CSSProperties;
  allowClear?: boolean;
  disabled?: boolean;
  requireSelection?: boolean;
};

const VendorAutoComplete = ({
  value,
  onChange,
  placeholder = 'Search vendors',
  style,
  allowClear,
  disabled,
  requireSelection = true,
}: Props) => {
  const { rawVendorData, vendorsLoading } = useContext(VendorLogoContext);
  const [searchTerm, setSearchTerm] = useState<string>(value || '');
  const [options, setOptions] = useState<Vendor[]>([]);

  useEffect(() => {
    if (isNotNull(rawVendorData)) {
      setOptions(rawVendorData);
    }
  }, [rawVendorData]);

  useEffect(() => {
    if (value !== searchTerm) {
      setSearchTerm(value || '');
    }
  }, [value]);

  const getVendorLabel = (vendor: Vendor) => vendor.name;

  const filterOptions = (search: string) => {
    const normalized = search.trim().toLowerCase();
    if (!normalized) {
      return rawVendorData || [];
    }

    return (rawVendorData || []).filter((vendor) => {
      const name = vendor.name?.toLowerCase() || '';
      const aliases = (vendor.aliases || []).map((alias) =>
        alias.toLowerCase(),
      );
      return (
        name.includes(normalized) ||
        aliases.some((alias) => alias.includes(normalized))
      );
    });
  };

  const handleSearch = (search: string) => {
    setSearchTerm(search);

    setOptions(filterOptions(search));
    if (!requireSelection) {
      onChange(search);
    }
  };

  const handleSelect = (selection: string, option: any) => {
    setSearchTerm(selection);

    onChange(option.value);
  };

  const handleBlur = () => {
    if (!requireSelection) {
      return;
    }

    const match = (rawVendorData || []).find(
      (vendor) => vendor.name === searchTerm,
    );

    if (!match) {
      setSearchTerm('');

      onChange('');
    }
  };

  const optionsData = useMemo(
    () =>
      options.map((vendor, index) => ({
        key: `${vendor.name}-${index}`,
        value: vendor.name,
        label: getVendorLabel(vendor),
      })),
    [options],
  );

  return (
    <AutoComplete
      value={searchTerm}
      options={optionsData}
      style={{ ...styles.input, ...style }}
      onSearch={handleSearch}
      onSelect={handleSelect}
      onBlur={handleBlur}
      placeholder={placeholder}
      disabled={disabled ?? false}
      notFoundContent={vendorsLoading ? 'Loading...' : 'No vendors found'}
    >
      <Input allowClear={allowClear} />
    </AutoComplete>
  );
};

export default VendorAutoComplete;
